'use es6';

import { Record } from 'immutable';
import { Networks } from 'AdsUI/lib/Constants';
import { formatAdWordsAccountId } from 'AdsUI/lib/Utils';
export const AdAccountStatus = {
  ACTIVE: 'ACTIVE',
  ON_HOLD: 'ON_HOLD',
  CLOSED: 'CLOSED',
  UNKNOWN: 'UNKNOWN'
};
export const AdAccountErrorStates = {
  NO_PRIMARY_USER: 'noPrimaryUser',
  NOT_AUTHENTICATED: 'notAuthenticated',
  MISSING_MANAGE_SCOPES: 'missingManageScope',
  MISSING_SCOPES: 'missingScopes',
  INACTIVE: 'inactive',
  PERMISSIONS_NEEDED: 'permsNeeded',
  LEAD_AD_PERMISSIONS_NEEDED: 'leadAdPermsNeeded',
  PAYMENT_METHOD_MISSING: 'paymentMethodMissing',
  CURRENT_USER_HAS_NOT_CONNECTED: 'currentUserHasNotConnected',
  NETWORK_API_CALLS_DISABLED: 'NETWORK_API_CALLS_DISABLED',
  NONE: 'none'
};
export const IGNORABLE_ACCOUNT_ERRORS_FOR_REPORTING = [AdAccountErrorStates.CURRENT_USER_HAS_NOT_CONNECTED, AdAccountErrorStates.LEAD_AD_PERMISSIONS_NEEDED, AdAccountErrorStates.MISSING_SCOPES // Missing Manage scopes, however, is not ignorable
];
export default class AdAccountRecord extends Record({
  network: null,
  accountId: null,
  accountName: null,
  connectedUser: null,
  connectedUserId: null,
  primaryHubSpotUserId: null,
  primaryNetworkUserId: null,
  connectedAt: null,
  currencyCode: null,
  trackingEnabled: null,
  trackingSince: null,
  accessibleToPrimaryUser: null,
  canCreateAds: null,
  canManageEntities: null,
  canCreateAudiences: null,
  paymentMethodIsMissing: null,
  currentUserHasConnected: null,
  status: AdAccountStatus.ACTIVE,
  adAccountAssignees: null,
  adAccountFetchError: null,
  conversionTrackingInfo: null
}) {
  static fromOverviewAndPermissions(overview, permissions) {
    return new AdAccountRecord(Object.assign({}, overview.toJS(), {}, permissions.toJS()));
  }

  isActive() {
    return this.status === AdAccountStatus.ACTIVE;
  }

  getDisplayName() {
    return this.network === Networks.ADWORDS ? this.getFormattedName() : this.getShortName();
  }

  getFormattedName() {
    const accountId = this.network === Networks.ADWORDS ? formatAdWordsAccountId(this.accountId) : this.accountId;
    return this.accountName ? `${this.accountName} (${accountId})` : accountId;
  }

  getShortName() {
    return this.network === Networks.ADWORDS && !this.accountName ? formatAdWordsAccountId(this.accountId) : this.accountName || this.accountId;
  }

  getAssignedIdsForType(type) {
    return this.adAccountAssignees.filter(assignee => assignee.assigneeType === type).map(assignee => assignee.id);
  }

  getErrorState(connectedUsers) {
    const primaryUserConnectedUser = connectedUsers.find(user => user.networkUserId === this.primaryNetworkUserId && user.network === this.network);

    if (!primaryUserConnectedUser) {
      return AdAccountErrorStates.NO_PRIMARY_USER;
    }

    if (!primaryUserConnectedUser.authenticated) {
      return AdAccountErrorStates.NOT_AUTHENTICATED;
    }

    if (primaryUserConnectedUser.isMissingManageScope()) {
      return AdAccountErrorStates.MISSING_MANAGE_SCOPES;
    }

    if (this.status !== AdAccountStatus.ACTIVE && this.status !== AdAccountStatus.UNKNOWN) {
      return AdAccountErrorStates.INACTIVE;
    }

    if (this.status !== AdAccountStatus.ACTIVE && this.status !== AdAccountStatus.UNKNOWN) {
      return AdAccountErrorStates.INACTIVE;
    }

    const currentUserConnectedUser = connectedUsers.find(user => user.networkUserId === this.connectedUserId && user.network === this.network);

    if (currentUserConnectedUser && currentUserConnectedUser.isMissingScopes()) {
      return AdAccountErrorStates.MISSING_SCOPES;
    }

    if (!this.accessibleToPrimaryUser) {
      return AdAccountErrorStates.PERMISSIONS_NEEDED;
    }

    if (this.paymentMethodIsMissing && this.network === Networks.FACEBOOK) {
      return AdAccountErrorStates.PAYMENT_METHOD_MISSING;
    }

    if (this.paymentMethodIsMissing && this.network === Networks.ADWORDS) {
      return AdAccountErrorStates.PAYMENT_METHOD_MISSING;
    }

    if (!this.currentUserHasConnected) {
      return AdAccountErrorStates.CURRENT_USER_HAS_NOT_CONNECTED;
    }

    if (!this.canCreateAds) {
      return AdAccountErrorStates.LEAD_AD_PERMISSIONS_NEEDED;
    }

    return AdAccountErrorStates.NONE;
  }

}